import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { profileService } from '../services/profile.service'
import LoadingSpinner from '../components/LoadingSpinner'
import akhiratLogo from '../assets/images/akhirat-logo.png'

/** Screen a temp-credentialed user (bulk-created or admin-issued password)
 *  lands on: they cannot proceed until they set their own password. */
export default function ForcedChangePasswordPage() {
  const { user, updateUser, logout } = useAuth()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPasswords, setShowPasswords] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const dashboardPathFor = (role?: string) =>
    role === 'ADMIN' ? '/admin' : role === 'FAMILY' || !role ? '/dashboard' : '/provider-dashboard'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match')
      return
    }
    setSubmitting(true)
    try {
      await profileService.changePassword({ currentPassword, newPassword, confirmPassword })
      updateUser({ ...(user as NonNullable<typeof user>), mustChangePassword: false })
      navigate(dashboardPathFor(user?.role), { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to set your password. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-10">
      <div className="mx-auto max-w-md px-4">
        <div className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex items-center gap-3">
            <img src={akhiratLogo} alt="Akhirat" className="h-10 w-10 object-contain" />
            <h1 className="text-lg font-bold text-neutral-900">Set your new password</h1>
          </div>

          <p className="mb-1 text-sm text-neutral-700">
            Assalamualaikum {user.firstName}, your account uses a temporary password.
          </p>
          <p className="mb-6 text-sm text-neutral-500">
            For your security, please set your own password before continuing. The temporary
            password will stop working after this.
          </p>

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="text-sm font-medium text-neutral-700">
                Temporary password
              </label>
              <input
                id="currentPassword"
                type={showPasswords ? 'text' : 'password'}
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-base shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="newPassword" className="text-sm font-medium text-neutral-700">
                New password
              </label>
              <input
                id="newPassword"
                type={showPasswords ? 'text' : 'password'}
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-base shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-neutral-500">
                At least 8 characters, with an uppercase letter, a lowercase letter, a number and a
                special character (e.g. @$!%*?&).
              </p>
            </div>
            <div>
              <label htmlFor="confirmPassword" className="text-sm font-medium text-neutral-700">
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                type={showPasswords ? 'text' : 'password'}
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-base shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-neutral-600">
              <input
                type="checkbox"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300 text-primary-600"
              />
              Show passwords
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Set password and continue'}
            </button>

            <button
              type="button"
              onClick={() => {
                logout()
                navigate('/login', { replace: true })
              }}
              className="w-full text-center text-sm text-neutral-500 hover:text-neutral-700"
            >
              Sign out instead
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
